import type { SiteContent } from "../types";

/**
 * 中文内容配置。改文案、加导航、加卡片只改本文件；
 * 类型由 src/types.ts 约束，结构与 en.ts 保持一致。
 */
export const zh: SiteContent = {
  meta: {
    title: "DOHORIZON — 让模型跨越地平线",
    description:
      "DOHORIZON 是一间专注落地的 AI Studio：定制化对话、模型微调与评测工具链，以及新架构研究。把 AI 从演示变成可度量、可迭代的日常生产力。",
    lang: "zh",
  },

  brand: "DOHORIZON",

  themeToggleLabel: "切换深浅色模式",

  nav: [
    { label: "动态", href: "#news" },
    { label: "模型", href: "#models" },
    { label: "服务", href: "#services" },
    { label: "研究", href: "#research" },
    { label: "关于", href: "#about" },
    { label: "联系", href: "#contact" },
  ],

  hero: {
    eyebrow: "AI Studio · 模型 / 服务 / 研究",
    titleLead: "让模型跨越地平线，",
    titleAccent: "落到真实场景里",
    lede: "我们做定制化对话、模型微调与评测工具链，也在新架构上持续研究——让 AGI 陪伴于人类。",
    ctas: [
      { label: "查看能力矩阵 →", href: "#services", variant: "solid" },
      { label: "预约一次项目沟通", href: "#contact", variant: "ghost" },
    ],
  },

  news: {
    id: "news",
    header: {
      eyebrow: "News",
      title: "最新动态",
      intro: "产品发布、研究进展与公司新闻。",
    },
    items: [
      {
        category: "发布",
        title: "DoHorizon-V1 发布：对话与角色中的工具调用",
        date: "2026-06",
        href: "#",
        featured: true,
      },
    ],
    moreLabel: "查看全部动态 →",
    moreHref: "#",
  },

  capabilities: {
    id: "services",
    header: {
      eyebrow: "Services",
      title: "我们提供的能力",
      intro: "从对话系统到训练评测，覆盖 AI 落地的完整链路。",
    },
    items: [
      { title: "定制对话", description: "围绕你的知识库与口径，搭建可控、可审计的对话系统。" },
      { title: "微调与评测", description: "端到端的训练、评测与部署流水线，每一步都有数字可看。" },
      { title: "架构研究", description: "小参数模型与多智能体协同，探索更轻、更稳的落地方式。" },
    ],
  },

  models: {
    id: "models",
    header: {
      eyebrow: "Models",
      title: "DoHorizon 模型家族",
      intro: "我们的首个模型——兼具多模态理解与共情、生活化的角色扮演。",
    },
    labels: { params: "参数量", context: "上下文", featured: "推荐" },
    items: [
      {
        name: "DoHorizon-V1",
        tagline: "多模态 · 角色扮演",
        params: "27B",
        context: "128K",
        tags: ["多模态理解", "角色扮演", "工具调用"],
        description: "27B 参数、128K 上下文，覆盖多模态理解与共情、生活化的角色扮演。",
        featured: true,
      },
    ],
  },

  story: {
    id: "story",
    kicker: "专题故事 · 与您谱写",
    title: "每一次交付，都有可度量的证据",
    lede: "一个真实客户案例的完整旅程——从评估、微调到上线，以及每个阶段背后的数字。",
    cta: { label: "阅读完整故事 →", href: "#", variant: "solid" },
  },

  research: {
    id: "research",
    header: {
      eyebrow: "Research",
      title: "我们在研究什么",
      intro: "把前沿探索沉淀为可复用的工程能力。",
    },
    items: [
      { area: "架构", title: "小参数高效架构", summary: "在更小的参数量下逼近大模型表现，降低部署门槛。", status: "进行中" },
      { area: "智能体", title: "多智能体协同", summary: "让多个专精智能体分工协作，稳定完成长链路任务。", status: "进行中" },
      { area: "评测", title: "可度量的对齐评测", summary: "构建面向业务的评测集与指标，让效果可量化、可回归。", status: "已发表" },
      { area: "推理", title: "低延迟推理优化", summary: "算子融合与量化，在同等硬件上提升吞吐、降低成本。", status: "进行中" },
    ],
  },

  values: {
    id: "values",
    quoteLead: "AI 的价值不在演示，",
    quoteAccent: "而在日常生产力",
    pillars: [
      { title: "可度量", description: "每次交付都有数字——可量化、可回归。" },
      { title: "可审计", description: "系统行为可控、可追溯；信任建立在透明之上。" },
      { title: "可迭代", description: "从第一天起就为持续演进而设计，而非一次性交付。" },
    ],
  },

  about: {
    id: "about",
    header: {
      eyebrow: "About",
      title: "横跨研究端与业务端",
    },
    paragraphs: [
      "DOHORIZON 是一间专注落地的 AI Studio。我们既做前沿模型与架构研究，也把成果打磨成企业可用的产品与服务。",
      "我们相信 AI 的价值不在演示，而在可度量、可迭代的日常生产力——因此每一次交付都带着数字。",
    ],
    stats: [
      { value: "3+", label: "自研模型规格" },
      { value: "20+", label: "落地项目" },
      { value: "99.9%", label: "服务可用性" },
      { value: "<200ms", label: "端侧响应" },
    ],
  },

  milestones: {
    id: "milestones",
    header: {
      eyebrow: "Milestones",
      title: "一路走来",
      intro: "从第一行代码到规模化部署的关键时刻。",
    },
    items: [
      { year: "2025", title: "DOHORIZON 成立", description: "以「从研究到业务」的路线图，从一间小工作室起步。" },
      { year: "2026", title: "DoHorizon 家族亮相", description: "发布 DoHorizon-V1——覆盖多模态理解与共情、生活化的角色扮演。" },
    ],
  },

  team: {
    id: "team",
    header: {
      eyebrow: "Team",
      title: "四个方向，一个目标",
      intro: "跨学科团队，陪研究走完通向生产的最后一公里。",
    },
    domains: [
      { name: "研究", description: "架构、对齐与评测方法的前沿工作。" },
      { name: "工程", description: "训练与推理基础设施——可复现、可扩展。" },
      { name: "产品", description: "把能力打包成可用、可度量的客户体验。" },
      { name: "运营", description: "交付、客户成功，以及让我们持续运转的系统。" },
    ],
  },

  careers: {
    id: "careers",
    title: "和我们一起，把模型带过地平线",
    intro: "我们正在寻找研究员、工程师与产品伙伴——无论你在哪个时区。",
    cta: { label: "加入我们（待定） →", href: "/coming-soon.html", variant: "solid" },
  },

  newsletter: {
    id: "newsletter",
    title: "订阅 DOHORIZON 通讯",
    intro: "每月一封——模型更新、研究笔记与案例分享。",
    placeholder: "your@email.com",
    submit: "订阅",
    success: "订阅成功——欢迎加入。",
    invalid: "请输入有效的邮箱地址。",
  },

  contact: {
    id: "contact",
    header: {
      eyebrow: "Contact",
      title: "聊聊你的场景",
      intro: "无论是定制对话、模型微调，还是一次技术评估，我们都乐于沟通。",
    },
    form: {
      name: "称呼",
      email: "邮箱",
      message: "想聊些什么？",
      submit: "发送需求",
      success: "已收到，我们会尽快与你联系。",
      invalid: "请填写称呼、有效邮箱与内容。",
    },
    info: { email: "contact@dohorizon.com", location: "中国 · 远程协作" },
  },

  footer: {
    copyright: `© ${new Date().getFullYear()} DOHORIZON`,
    tagline: "横跨研究端 → 业务端",
    columns: [
      {
        title: "产品与服务",
        links: [
          { label: "模型", href: "#models" },
          { label: "服务", href: "#services" },
          { label: "动态", href: "#news" },
        ],
      },
      {
        title: "研究与技术",
        links: [
          { label: "研究方向", href: "#research" },
          { label: "专题故事", href: "#story" },
          { label: "技术博客", href: "#" },
        ],
      },
      {
        title: "关于我们",
        links: [
          { label: "公司介绍", href: "#about" },
          { label: "价值观", href: "#values" },
          { label: "加入我们（待定）", href: "/coming-soon.html" },
          { label: "联系", href: "#contact" },
        ],
      },
    ],
    legal: [
      { label: "隐私政策", href: "#" },
      { label: "服务条款", href: "#" },
    ],
  },
};
